import { simplifyError } from '@/utils/errorMessage';
import { accountTimeLabel } from '@/utils/accountTimeZone';
/* eslint-disable react/function-component-definition */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import Cookies from 'js-cookie';
import type DynamsoftWorker from './dynamsoftWorker';
import { API_BASE_URL } from '@/utils/apiFetch';

interface DynamsoftScanListenerProps {
  onScanTriggered?: (command: any) => void;
  onScanComplete?: (result: any) => void;
  hideUI?: boolean;
}

const DynamsoftScanListener: React.FC<DynamsoftScanListenerProps> = ({ onScanTriggered, onScanComplete, hideUI = false }) => {
  const connectionRef = useRef<HubConnection | null>(null);
  const dynamsoftWorkerRef = useRef<DynamsoftWorker | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [debugVisible, setDebugVisible] = useState(false);
  const [scanStatus, setScanStatus] = useState('Ready');
  const [lastCommand, setLastCommand] = useState<any>(null);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [currentScanCommand, setCurrentScanCommand] = useState<any>(null);
  const [scannedPages, setScannedPages] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // 🚀 FIX: Add protection against multiple simultaneous scans
  const [isScanInProgress, setIsScanInProgress] = useState(false);
  const scanLockRef = useRef(false);

  // 🚀 NEW: Track accumulated pages for batch creation
  const [accumulatedPages, setAccumulatedPages] = useState(0);
  const [hasScannedContent, setHasScannedContent] = useState(false);

  // Use consistent container ID
  const containerId = 'scannerContainer';

  useEffect(() => {
    const initializeSignalR = async () => {
      try {
        // Create connection to the correct AgentChatHub
        const connection = new HubConnectionBuilder()
          .withUrl(`${API_BASE_URL}/api/agent-chat-hub`, {
            withCredentials: true,
            transport: 1, // WebSockets
            accessTokenFactory: () => localStorage.getItem('jwt_token') || ''
          })
          .withAutomaticReconnect()
          .build();

        // Listen for scan commands
        connection.on('ReceiveScanCommand', (command) => {
          setLastCommand(command);
          setCurrentScanCommand(command);
          if (!hideUI) {
            setDebugVisible(true);
            setScanModalOpen(true);
          }
          
          // Call the callback if provided
          if (onScanTriggered) {
            onScanTriggered(command);
          }
          
          handleScanCommand(command);
        });

        // Listen for connection state changes
        connection.onclose(() => {
          setIsInitialized(false);
        });

        connection.onreconnecting(() => {
          setIsInitialized(false);
        });

        connection.onreconnected(() => {
          setIsInitialized(true);
        });

        // Start the connection
        await connection.start();

        connectionRef.current = connection;
        setIsInitialized(true);
        setScanStatus('Connected to AgentChatHub');
              } catch (error) {
          console.error('❌ Failed to initialize SignalR connection:', error);
          setIsInitialized(false);
          setScanStatus(simplifyError(error, 'Unable to connect to the scanner. Check the connection and try again.'));
        }
    };

    initializeSignalR();

    // Cleanup on unmount
    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, [onScanTriggered, hideUI]);

  const initializeDynamsoftWorker = async () => {
    try {
      if (dynamsoftWorkerRef.current) {
        dynamsoftWorkerRef.current.cleanup();
      }

      setScanStatus('Initializing Dynamsoft...');

      // 🚀 IMPROVED: Better container validation
      if (!hideUI) {
        const container = document.getElementById(containerId);
        if (!container) {
          throw new Error(`Container ${containerId} not found before initialization`);
        }
      }

      // Create proper theme object
      const theme = {
        palette: {
          background: { paper: '#ffffff' },
          action: { selected: '#e3f2fd', hover: '#f5f5f5' },
          primary: { main: '#1976d2' },
          text: { secondary: '#666666' }
        }
      };

      // Initialize DynamsoftWorker with the container that exists in the modal.
      // Loaded on demand (first scan) so the heavy TWAIN worker + dwt bootstrap stay
      // out of the app entry chunk; the SignalR listener above keeps running so scan
      // commands are still received immediately.
      const { default: DynamsoftWorkerClass } = await import('./dynamsoftWorker');
      const worker = new DynamsoftWorkerClass(containerId, theme);
      await worker.initialize();
      dynamsoftWorkerRef.current = worker;

      // 🚀 IMPROVED: Enhanced buffer validation and clearing
      try {
        const dwObject = worker.getDWObject();
        if (dwObject && typeof dwObject.HowManyImagesInBuffer === 'function') {
          const bufferCount = dwObject.HowManyImagesInBuffer;
          if (bufferCount > 0) {
            dwObject.RemoveAllImages();
          }
        }
      } catch (bufferError) {}

      setIsInitialized(true);
      setScanStatus('Dynamsoft Ready');

      return worker;
    } catch (error) {
      console.error('❌ Failed to initialize DynamsoftWorker:', error);
      setScanStatus(simplifyError(error, 'Unable to start the scanner. Please try again.'));
      setIsInitialized(false);
      throw error;
    }
  };

  const handleScanCommand = async (command: any) => {
    // 🚀 FIX: Prevent multiple simultaneous scans
    if (scanLockRef.current || isScanInProgress) {
      setScanStatus('Scan already in progress - ignoring duplicate');
      return;
    }

    try {
      // Set scan lock
      scanLockRef.current = true;
      setIsScanInProgress(true);

      // Wait for modal to be rendered and container to exist
      let attempts = 0;
      while (!document.getElementById(containerId) && attempts < 20) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      if (!document.getElementById(containerId)) {
        throw new Error(`Container ${containerId} not found after waiting`);
      }

      // 🚀 IMPROVED: Better container readiness check
      if (!hideUI) {
        let containerReady = false;
        let readyAttempts = 0;

        while (!containerReady && readyAttempts < 10) {
          const container = document.getElementById(containerId);
          if (container) {
            const rect = container.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              containerReady = true;
            } else {
              await new Promise(resolve => setTimeout(resolve, 200));
              readyAttempts++;
            }
          }
        }
      }

      // 🚀 IMPROVED: Worker validation with health check
      let needsReinit = false;
      
      if (!dynamsoftWorkerRef.current || !isInitialized) {
        needsReinit = true;
      } else {
        // Quick health check of existing worker
        try {
          const dwObject = dynamsoftWorkerRef.current.getDWObject();
          if (!dwObject || typeof dwObject.HowManyImagesInBuffer !== 'function') {
            needsReinit = true;
          } else {
            // Test a basic operation
            dwObject.HowManyImagesInBuffer;
          }
        } catch (healthError) {
          needsReinit = true;
        }
      }

      if (needsReinit) {
        await initializeDynamsoftWorker();
      }

      if (!dynamsoftWorkerRef.current) {
        throw new Error('DynamsoftWorker initialization failed');
      }

      setScanStatus('Scanning...');

      // 🚀 IMPROVED: Retry mechanism for scan operation
      let scanSuccess = false;
      let scanAttempts = 0;
      const maxScanAttempts = 2;
      let lastError = null;
      
      while (!scanSuccess && scanAttempts < maxScanAttempts) {
        try {
          scanAttempts++;
          if (scanAttempts > 1) {
            setScanStatus(`Retrying scan... (${scanAttempts}/${maxScanAttempts})`);
            // Brief pause before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          // Use DynamsoftWorker to handle scanning
          const scanResult = await dynamsoftWorkerRef.current.handleScan(0, null, command.pages, async () => {
            // Get actual pages scanned from buffer
            const totalPagesInBuffer = dynamsoftWorkerRef.current?.getDWObject()?.HowManyImagesInBuffer || 0;

            // 🚀 NEW: Just update the page count - don't create document yet
            setAccumulatedPages(totalPagesInBuffer);
            setHasScannedContent(totalPagesInBuffer > 0);
            setScannedPages(totalPagesInBuffer);

            // Don't create or upload document here - wait for user to close modal

            if (hideUI) {
              // In headless mode we finalize automatically
              await closeScanModal(command);
            }
          },
          !hideUI // Show scanner UI when not in headless mode
          );

          if ((scanResult as any)?.success) {
            scanSuccess = true;
          } else {
            lastError = new Error((scanResult as any)?.error || 'Scan failed');
            console.warn(`⚠️ Scan attempt ${scanAttempts} failed:`, lastError.message);
            
            if (scanAttempts >= maxScanAttempts) {
              throw lastError;
            }
          }
        } catch (attemptError) {
          lastError = attemptError;
          console.warn(`⚠️ Scan attempt ${scanAttempts} failed:`, attemptError.message);
          
          if (scanAttempts >= maxScanAttempts) {
            throw attemptError;
          }
          
          // For certain errors, try reinitializing the worker before retry
          if (attemptError.message.includes('scanner') || attemptError.message.includes('source')) {
            try {
              await initializeDynamsoftWorker();
            } catch (reinitError) {
              console.warn('⚠️ Worker reinitialization failed:', reinitError.message);
            }
          }
        }
      }

    } catch (error) {
      console.error('❌ Scan operation failed:', error);
      setScanStatus(simplifyError(error, 'The scan could not finish. Check the scanner and try again.'));
      setIsUploading(false);
      
      if (onScanComplete) {
        onScanComplete({ success: false, error: error.message });
      }
    } finally {
      // Always release the scan lock
      scanLockRef.current = false;
      setIsScanInProgress(false);
    }
  };

  const closeScanModal = async (commandOverride?: any) => {
    const activeCommand = commandOverride || currentScanCommand;

    if (!activeCommand) {
      console.warn('closeScanModal called without active command. Closing UI and resetting state.');
      setScanModalOpen(false);
      setDebugVisible(false);
      setAccumulatedPages(0);
      setHasScannedContent(false);
      return;
    }

    try {
      // 🚀 Determine page count if headless or state not yet updated
      let pagesToProcess = accumulatedPages;
      // In headless mode, state update for pages might not have propagated yet. Read directly from worker.
      if (hideUI && pagesToProcess === 0 && dynamsoftWorkerRef.current) {
        pagesToProcess = dynamsoftWorkerRef.current.getDWObject()?.HowManyImagesInBuffer || 0;
        if (pagesToProcess > 0) {
          // It's good to set state anyway for consistency, even if we don't use it immediately
          setAccumulatedPages(pagesToProcess);
          setHasScannedContent(true);
        }
      }

      // 🚀 Create document with all pages when auto-closing or user closes modal
      if (pagesToProcess > 0 && activeCommand) {
        setIsUploading(true);
        setScanStatus(`Creating document with ${pagesToProcess} pages...`);
        
        // Get existing documents to calculate proper order
        let nextOrder = 1;
        try {
          const token = localStorage.getItem('jwt_token');
          const response = await fetch(`/api/capture/documents/${activeCommand.batchId}`, {
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
          });
          if (response.ok) {
            const existingDocs = await response.json();
            if (Array.isArray(existingDocs) && existingDocs.length > 0) {
              const maxOrder = Math.max(...existingDocs.map((doc: any) => doc.Order || doc.order || 0));
              nextOrder = maxOrder + 1;
            }
          }
        } catch (error) {
          console.warn('Failed to fetch existing documents, using order 1:', error);
        }
        
        // Create single document with all accumulated pages
        const docId = crypto.randomUUID();
        const doc = {
          docId: docId,
          name: `Agent Scan ${nextOrder} - ${pagesToProcess} pages - ${accountTimeLabel(new Date())}`,
          pages: 0,
          storageId: null,
          uploaded: false,
          needsUpload: false,
          order: nextOrder,
          intentionallyEmpty: false,
          isEmpty: true,
          wasModified: false,
          isNew: true,
          error: null,
          uploading: false
        };

        // Save document to get storageId
        const saveResult = await dynamsoftWorkerRef.current!.saveDocument(docId, doc);

        if (saveResult.success && saveResult.storageId) {
          // Prepare document for upload
          const docToUpload = {
            ...doc,
            storageId: saveResult.storageId,
            pages: saveResult.currentPages,
            needsUpload: true,
            isEmpty: false,
            wasModified: true,
            isNew: false
          };

          const uploadResult = await dynamsoftWorkerRef.current!.uploadDocument(
            docToUpload,
            activeCommand.batchId,
            () => {}
          );

          if (uploadResult?.success) {
            setScanStatus(`Upload Complete - 1 document, ${saveResult.currentPages} pages`);
          } else {
            throw new Error(uploadResult?.error || 'Upload failed');
          }
        } else {
          throw new Error(saveResult.error || 'Save failed');
        }
        
        setIsUploading(false);
      }

      // Send completion signal to server to start the workflow task
      if (connectionRef.current && activeCommand) {
        await connectionRef.current.invoke('ScanCompleted', {
          success: true,
          message: pagesToProcess > 0 ? 
            `Scan and upload completed successfully - ${pagesToProcess} pages` : 
            'Scan session completed (no pages scanned)',
          batchId: activeCommand.batchId,
          chatId: activeCommand.chatId
        });

        // Call completion callback
        if (onScanComplete && pagesToProcess > 0) {
          onScanComplete({ 
            success: true, 
            batchId: activeCommand.batchId,
            chatId: activeCommand.chatId,
            pages: pagesToProcess
          });
        }
      }
    } catch (signalError) {
      console.error('❌ Failed to create document or send completion signal:', signalError);
      setScanStatus(simplifyError(signalError, 'Unable to save the scan. Please try again.'));
      setIsUploading(false);
    }

    // Reset states
    setCurrentScanCommand(null);
    setScanModalOpen(false);
    setScanStatus('Ready');
    setDebugVisible(false);
    setAccumulatedPages(0);
    setHasScannedContent(false);
  };

  const testScan = async () => {
    // Use the last real scan command context if available, otherwise use test values
    const testCommand = {
      action: 'triggerScan',
      batchId: lastCommand?.batchId || 'test-batch-id',
      chatId: lastCommand?.chatId || 'test-chat-id',
      quality: 'high',
      pages: 10,
      timestamp: new Date()
    };

    setCurrentScanCommand(testCommand);
    setScanModalOpen(true);

    // Handle scan command after modal opens
    setTimeout(async () => {
      await handleScanCommand(testCommand);
    }, 200);
  };

  const initializeForTesting = async () => {
    try {
      // First check if modal is open and container exists
      if (!scanModalOpen) {
        setScanModalOpen(true);
        // Wait for modal to render
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      await initializeDynamsoftWorker();
    } catch (error) {
      console.error('❌ Failed to initialize for testing:', error);
    }
  };

  return (
    <>
      {/* Hidden container in headless mode */}
      {hideUI && (
        <div
          id={containerId}
          style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', visibility: 'hidden' }}
        />
      )}

      {/* UI elements only if not in headless mode */}
      {!hideUI && (
        <>
          {/* Scan Modal Dialog */}
          {scanModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-6xl flex flex-col">
                {/* Modal Header */}
                <div className="flex justify-between items-center p-4 border-b">
                  <h2 className="text-xl font-semibold text-gray-800">Document Scanner</h2>
                  <button onClick={() => closeScanModal()} className="text-gray-500 hover:text-gray-700 text-2xl">
                    ×
                  </button>
                </div>

                {/* Modal Body - Dynamsoft Viewer */}
                <div className="flex-1 p-4">
                  <div className="w-full h-full border border-gray-300 rounded">
                    <div id={containerId} className="w-full h-full opacity-100" />
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Status:</span> {scanStatus}
                    {accumulatedPages > 0 && (
                      <span className="ml-4">
                        <span className="font-medium">Accumulated Pages:</span> {accumulatedPages}
                      </span>
                    )}
                    {isUploading && (
                      <span className="ml-4 text-blue-600">
                        <span className="font-medium">Uploading...</span>
                      </span>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={testScan}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded transition-colors"
                      disabled={isScanInProgress}
                      title={isScanInProgress ? 'Scan in progress...' : (lastCommand?.batchId ? `Test with real batch: ${lastCommand.batchId}` : 'Test with dummy batch (may fail upload)')}
                    >
                      {isScanInProgress ? 'Scanning...' : `Test Scan ${lastCommand?.batchId ? '(Real)' : '(Test)'}`}
                    </button>

                    <button
                      onClick={initializeForTesting}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
                    >
                      Initialize Dynamsoft
                    </button>

                    <button
                      onClick={() => closeScanModal()}
                      className={`px-4 py-2 rounded transition-colors ${
                        hasScannedContent 
                          ? 'bg-red-500 hover:bg-red-600 text-white' 
                          : 'bg-gray-500 hover:bg-gray-600 text-white'
                      }`}
                      disabled={isUploading}
                    >
                      {isUploading ? 'Creating Document...' : hasScannedContent ? `Close & Create Document (${accumulatedPages} pages)` : 'Close'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Debug floating button */}
          <div className="fixed bottom-4 right-4 z-50">
            <button
              onClick={() => setDebugVisible(!debugVisible)}
              className="bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition-colors"
              title="Dynamsoft Scanner Debug"
            >
              🖨️
            </button>
          </div>

          {/* Debug panel - BIGGER */}
          {debugVisible && (
            <div className="fixed bottom-20 right-4 bg-white border border-gray-300 rounded-lg shadow-xl p-4 z-50 w-96 max-h-[600px] overflow-y-auto">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-800">Scanner Debug</h3>
                <button onClick={() => setDebugVisible(false)} className="text-gray-500 hover:text-gray-700">
                  ✕
                </button>
              </div>

              <div className="space-y-3 text-sm">
                <div>
                  <strong>Status:</strong>
                  <span className={`ml-2 ${scanStatus.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                    {scanStatus}
                  </span>
                </div>

                <div>
                  <strong>Initialized:</strong>
                  <span className={`ml-2 ${isInitialized ? 'text-green-600' : 'text-red-600'}`}>
                    {isInitialized ? 'Yes' : 'No'}
                  </span>
                </div>

                <div>
                  <strong>Container Exists:</strong>
                  <span className={`ml-2 ${document.getElementById(containerId) ? 'text-green-600' : 'text-red-600'}`}>
                    {document.getElementById(containerId) ? 'Yes' : 'No'}
                  </span>
                </div>

                <div>
                  <strong>Modal Open:</strong>
                  <span className={`ml-2 ${scanModalOpen ? 'text-green-600' : 'text-red-600'}`}>
                    {scanModalOpen ? 'Yes' : 'No'}
                  </span>
                </div>

                <div>
                  <strong>Scan In Progress:</strong>
                  <span className={`ml-2 ${isScanInProgress ? 'text-yellow-600' : 'text-green-600'}`}>
                    {isScanInProgress ? 'Yes' : 'No'}
                  </span>
                </div>

                <div>
                  <strong>Scan Lock:</strong>
                  <span className={`ml-2 ${scanLockRef.current ? 'text-red-600' : 'text-green-600'}`}>
                    {scanLockRef.current ? 'LOCKED' : 'FREE'}
                  </span>
                </div>

                <div>
                  <strong>Accumulated Pages:</strong>
                  <span className={`ml-2 ${accumulatedPages > 0 ? 'text-blue-600' : 'text-gray-600'}`}>
                    {accumulatedPages}
                  </span>
                </div>

                <div>
                  <strong>Has Content:</strong>
                  <span className={`ml-2 ${hasScannedContent ? 'text-green-600' : 'text-gray-600'}`}>
                    {hasScannedContent ? 'Yes' : 'No'}
                  </span>
                </div>

                <div>
                  <strong>SignalR Connected:</strong>
                  <span
                    className={`ml-2 ${connectionRef.current?.state === 'Connected' ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {connectionRef.current?.state || 'Unknown'}
                  </span>
                </div>

                {lastCommand && (
                  <div>
                    <strong>Last Scan Command:</strong>
                    <pre className="text-xs bg-green-100 p-2 rounded mt-1 overflow-x-auto">
                      {JSON.stringify(lastCommand, null, 2)}
                    </pre>
                  </div>
                )}

                <div>
                  <strong>Connection Details:</strong>
                  <div className="text-xs bg-gray-100 p-2 rounded mt-1">
                    <div>Hub: AgentChatHub</div>
                    <div>URL: {API_BASE_URL}/agent-chat-hub</div>
                    <div>Transport: WebSockets</div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-2 border-t">
                  <button
                    onClick={initializeForTesting}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Initialize Dynamsoft
                  </button>
                  <button
                    onClick={testScan}
                    className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
                    disabled={!isInitialized || isScanInProgress}
                    title={isScanInProgress ? 'Scan in progress...' : (lastCommand?.batchId ? `Test with real batch: ${lastCommand.batchId}` : 'Test with dummy batch (may fail upload)')}
                  >
                    {isScanInProgress ? 'Scanning...' : `Test Scan ${lastCommand?.batchId ? '(Real)' : '(Test)'}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default DynamsoftScanListener;
